import { z } from 'zod'
import { supabaseClient } from '~/shared/common/supabase/client'
import type { ShareableListData } from '~/server/shareable-list/shareable-list.service'
import {
  AddShareableListMembershipInput,
  AddShareableListMembershipOutput,
  CloneShareableListInput,
  CloneShareableListOutput,
  CreateShareableListInput,
  CreateShareableListOutput,
  DeleteShareableListInput,
  DeleteShareableListOutput,
  RemoveShareableListMembershipInput,
  RemoveShareableListMembershipOutput,
  ReorderShareableListMembershipsInput,
  ReorderShareableListMembershipsOutput,
  UpdateShareableListInput,
  UpdateShareableListMembershipInput,
  UpdateShareableListMembershipOutput,
  UpdateShareableListOutput,
} from '~/shared/shareable-list/shareable-list.api.schema'

const getAuthHeader = async (): Promise<string> => {
  const {
    data: { session },
  } = await supabaseClient.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }
  return `Bearer ${session.access_token}`
}

const fetchJson = async <T>(
  path: string,
  body: unknown,
  parser: z.ZodType<T>,
): Promise<T> => {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthHeader(),
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const errBody = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(errBody.error ?? `${path} ${res.status}`)
  }
  return parser.parse(await res.json())
}

export const fetchShareableListBySlug = async (slug: string): Promise<ShareableListData> => {
  const res = await fetch(`/api/getShareableListBySlug?slug=${encodeURIComponent(slug)}`)
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error || 'Failed to fetch shareable list')
  }
  return (await res.json()) as ShareableListData
}

export const submitCreateShareableList = (input: z.infer<typeof CreateShareableListInput>) =>
  fetchJson('/api/shareable-list/create', input, CreateShareableListOutput)

export const submitCloneShareableList = (input: z.infer<typeof CloneShareableListInput>) =>
  fetchJson('/api/shareable-list/clone', input, CloneShareableListOutput)

export const submitUpdateShareableList = (input: z.infer<typeof UpdateShareableListInput>) =>
  fetchJson('/api/shareable-list/update', input, UpdateShareableListOutput)

export const submitDeleteShareableList = (input: z.infer<typeof DeleteShareableListInput>) =>
  fetchJson('/api/shareable-list/delete', input, DeleteShareableListOutput)

export const submitAddShareableListMembership = (input: z.infer<typeof AddShareableListMembershipInput>) =>
  fetchJson('/api/shareable-list/membership/add', input, AddShareableListMembershipOutput)

export const submitRemoveShareableListMembership = (input: z.infer<typeof RemoveShareableListMembershipInput>) =>
  fetchJson('/api/shareable-list/membership/remove', input, RemoveShareableListMembershipOutput)

export const submitUpdateShareableListMembership = (input: z.infer<typeof UpdateShareableListMembershipInput>) =>
  fetchJson('/api/shareable-list/membership/update', input, UpdateShareableListMembershipOutput)

export const submitReorderShareableListMemberships = (input: z.infer<typeof ReorderShareableListMembershipsInput>) =>
  fetchJson('/api/shareable-list/membership/reorder', input, ReorderShareableListMembershipsOutput)
